import type {PullRequestPickerPullRequest} from '@github-ui/item-picker/PullRequestAndBranchPicker'
import {
  DOCUMENT_FIELDS_PATH,
  type EntityOperation,
  isEntityOperation,
  type SetFieldsOperation,
} from '@github-ui/optimistic-connection'
import {ENTITY_FIELDS_OWNER} from '@github-ui/optimistic-connection/pendingOperations'
import {notifySettlement} from '@github-ui/optimistic-connection/settlementRegistry'
import {useConnectionMutation} from '@github-ui/optimistic-connection/useConnectionMutation'
import {useEntityMutation} from '@github-ui/optimistic-connection/useEntityMutation'
import {linkPullRequestsMutation as linkPullRequestsQueryId} from '@github-ui/server-persisted-queries'
import type {LinkPullRequestsMutationMutation} from '@github-ui/server-persisted-queries/types/linkPullRequestsMutation'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useCallback, useRef} from 'react'
import {useRelayEnvironment} from 'react-relay'
import {commitLocalUpdate, type Disposable, type RecordSourceProxy} from 'relay-runtime'

type LinkPullRequestsMutationParams = {
  issueId: string
  owner: string
  repo: string
  issueNumber: number
  linkedPullRequests: PullRequestPickerPullRequest[]
  onError: (error: Error) => void
  onCompleted?: () => void
}

type OptimisticLinkedPullRequests = {
  nodes: PullRequestPickerPullRequest[]
}

type DashboardPullRequestChanges = {
  added: PullRequestPickerPullRequest[]
  removed: PullRequestPickerPullRequest[]
}

type DashboardUpdate = {
  confirm: (pullRequests: ReadonlyArray<LinkedPullRequest | null>) => Promise<void>
  rollback: (error: Error) => void
}

type Deferred<T> = {
  promise: Promise<T>
  resolve: (value: T | PromiseLike<T>) => void
  reject: (reason?: unknown) => void
}

type MutationContext = {
  dashboardUpdate?: DashboardUpdate
  relayUpdate: Disposable
}

function buildOptimisticOperation(
  issueId: string,
  linkingPullRequests: PullRequestPickerPullRequest[],
): SetFieldsOperation {
  return {
    type: 'setFields',
    id: issueId,
    fields: {closedByPullRequestsReferences: {nodes: linkingPullRequests}},
  }
}

function linkingPullRequestsFromOperation(operation: SetFieldsOperation): PullRequestPickerPullRequest[] {
  const linkedPullRequests = operation.fields.closedByPullRequestsReferences as OptimisticLinkedPullRequests
  return linkedPullRequests.nodes
}

function changesBetween(
  current: PullRequestPickerPullRequest[],
  next: PullRequestPickerPullRequest[],
): DashboardPullRequestChanges {
  const currentIds = new Set(current.map(pullRequest => pullRequest.id))
  const nextIds = new Set(next.map(pullRequest => pullRequest.id))
  return {
    added: next.filter(pullRequest => !currentIds.has(pullRequest.id)),
    removed: current.filter(pullRequest => !nextIds.has(pullRequest.id)),
  }
}

function toDashboardLinkedPullRequestsOperation({
  issueId,
  added,
  removed,
}: DashboardPullRequestChanges & {issueId: string}): EntityOperation {
  return {
    type: 'updateEntityFieldItems',
    id: issueId,
    field: 'linkedPullRequests',
    upserts: added.map(item => ({key: item.url, item})),
    removeKeys: removed.map(item => item.url),
    countDelta: added.length - removed.length,
  }
}

type ConfirmedIssue = Extract<
  NonNullable<NonNullable<LinkPullRequestsMutationMutation['linkIssueOrPullRequest']>['baseIssueOrPullRequest']>,
  {__typename: 'Issue'}
>
type ConfirmedPullRequestNodes = NonNullable<NonNullable<ConfirmedIssue['closedByPullRequestsReferences']>['nodes']>
type LinkedPullRequest = PullRequestPickerPullRequest | NonNullable<ConfirmedPullRequestNodes[number]>

let dashboardOperationSequence = 0

async function ignoreMutationFailure(mutation: Promise<unknown>): Promise<void> {
  try {
    await mutation
  } catch {
    // The primary mutation reports this same failure to the caller.
  }
}

function useDashboardLinkedPullRequestsUpdate(issueId: string, linkedPullRequests: PullRequestPickerPullRequest[]) {
  const deferredsRef = useRef(new WeakMap<EntityOperation, Deferred<EntityOperation>>())
  const latestSelectionRef = useRef(linkedPullRequests)
  const activeUpdateCountRef = useRef(0)
  const {mutateAsync} = useConnectionMutation<EntityOperation>({
    ownerId: ENTITY_FIELDS_OWNER,
    connectionPath: DOCUMENT_FIELDS_PATH,
    scope: {id: `link-pull-requests-dashboard:${issueId}`},
    mutationFn: operation => {
      if (!isEntityOperation(operation)) throw new Error('Dashboard update must target entity fields')
      const deferred = deferredsRef.current.get(operation)
      if (!deferred) throw new Error('Dashboard linked pull request update was not registered')
      return deferred.promise
    },
    onSuccess: (operation, variables) =>
      notifySettlement(operation, ENTITY_FIELDS_OWNER, DOCUMENT_FIELDS_PATH, variables.operationId, {
        forceRevalidate: true,
      }),
  })

  return useCallback(
    (next: PullRequestPickerPullRequest[]): DashboardUpdate => {
      const previous = activeUpdateCountRef.current === 0 ? linkedPullRequests : latestSelectionRef.current
      latestSelectionRef.current = next
      activeUpdateCountRef.current += 1
      const changes = changesBetween(previous, next)
      let finished = false
      const finish = () => {
        if (finished) return
        finished = true
        activeUpdateCountRef.current = Math.max(0, activeUpdateCountRef.current - 1)
      }

      if (changes.added.length === 0 && changes.removed.length === 0) {
        return {
          confirm: async () => finish(),
          rollback: () => finish(),
        }
      }

      const operation = toDashboardLinkedPullRequestsOperation({issueId, ...changes})
      const deferred = Promise.withResolvers<EntityOperation>()
      deferredsRef.current.set(operation, deferred)
      const mutation = mutateAsync({
        operation,
        operationId: `link-pull-requests-dashboard-${dashboardOperationSequence++}`,
      })
      void ignoreMutationFailure(mutation)
      return {
        confirm: async pullRequests => {
          try {
            const confirmedIds = new Set(pullRequests.flatMap(pullRequest => (pullRequest?.id ? [pullRequest.id] : [])))
            const added = changes.added.map(addedPullRequest => {
              const confirmed = pullRequests.find(pullRequest => pullRequest?.id === addedPullRequest.id)
              return confirmed ? {...addedPullRequest, ...confirmed} : addedPullRequest
            })
            const removed = changes.removed.filter(removedPullRequest => !confirmedIds.has(removedPullRequest.id))
            deferred.resolve(toDashboardLinkedPullRequestsOperation({issueId, added, removed}))
            await mutation
          } finally {
            finish()
          }
        },
        rollback: error => {
          deferred.reject(error)
          finish()
        },
      }
    },
    [issueId, linkedPullRequests, mutateAsync],
  )
}

function writeRelayLinkedPullRequests(
  store: RecordSourceProxy,
  issueId: string,
  pullRequests: ReadonlyArray<LinkedPullRequest | null>,
): void {
  const issue = store.get(issueId)
  if (!issue) return

  const records = pullRequests.flatMap(pullRequest => {
    if (!pullRequest) return []
    const record = store.get(pullRequest.id) ?? store.create(pullRequest.id, 'PullRequest')
    for (const field of ['id', 'url', 'number', 'title', 'state', 'isDraft', 'isInMergeQueue', 'createdAt'] as const) {
      record.setValue(pullRequest[field], field)
    }

    const repository = pullRequest.repository
    const repositoryRecord = store.get(repository.id) ?? store.create(repository.id, 'Repository')
    repositoryRecord.setValue(repository.id, 'id')
    repositoryRecord.setValue(repository.name, 'name')
    repositoryRecord.setValue(repository.nameWithOwner, 'nameWithOwner')

    const owner = repository.owner
    const ownerRecord = store.get(owner.id) ?? store.create(owner.id, owner.__typename)
    ownerRecord.setValue(owner.id, 'id')
    ownerRecord.setValue(owner.login, 'login')
    repositoryRecord.setLinkedRecord(ownerRecord, 'owner')
    record.setLinkedRecord(repositoryRecord, 'repository')
    return [record]
  })

  for (const first of [10, 25]) {
    issue
      .getLinkedRecord('closedByPullRequestsReferences', {first, includeClosedPrs: true})
      ?.setLinkedRecords(records.slice(0, first), 'nodes')
  }
  issue
    .getLinkedRecord('closedByPullRequestsReferences', {
      first: 10,
      includeClosedPrs: false,
      orderByState: true,
    })
    ?.setLinkedRecords(records.filter(record => record.getValue('state') !== 'CLOSED').slice(0, 10), 'nodes')
}

async function linkPullRequestsRequest({
  owner,
  repo,
  issueNumber,
  linkingIds,
}: Pick<LinkPullRequestsMutationParams, 'owner' | 'repo' | 'issueNumber'> & {
  linkingIds: string[]
}): Promise<LinkPullRequestsMutationMutation> {
  const query = new URLSearchParams({queryId: linkPullRequestsQueryId})
  const response = await reactFetchJSON(
    `/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/issues/${issueNumber}/closed_by_pull_requests_references.json?${query}`,
    {method: 'PUT', body: {linkingIds}},
  )

  let result:
    | {data?: LinkPullRequestsMutationMutation | null; errors?: ReadonlyArray<{message?: string}> | null}
    | undefined
  try {
    result = (await response.json()) as typeof result
  } catch {
    result = undefined
  }

  const error = result?.errors?.find(item => item.message)?.message
  const data = result?.data
  const issue = data?.linkIssueOrPullRequest?.baseIssueOrPullRequest
  if (!response.ok || error || !data || issue?.__typename !== 'Issue') {
    throw new Error(error ?? 'Failed to link pull requests')
  }
  return data
}

export function useLinkPullRequestsMutation({
  issueId,
  owner,
  repo,
  issueNumber,
  linkedPullRequests,
  onError,
  onCompleted,
}: LinkPullRequestsMutationParams) {
  const environment = useRelayEnvironment()
  const updateDashboard = useDashboardLinkedPullRequestsUpdate(issueId, linkedPullRequests)
  const {mutate} = useEntityMutation<LinkPullRequestsMutationMutation, Error, MutationContext>({
    scope: {id: `link-pull-requests:${issueId}`},
    mutationFn: operation => {
      const linkingIds = linkingPullRequestsFromOperation(operation).map(pullRequest => pullRequest.id)
      return linkPullRequestsRequest({owner, repo, issueNumber, linkingIds})
    },
    // RELAY-CLEANUP(LinkedPullRequests): mirror the primary header until it reads the TanStack document.
    onMutate: ({operation}) => {
      const optimisticPullRequests = linkingPullRequestsFromOperation(operation)
      const relayUpdate = environment.applyUpdate({
        storeUpdater: store => writeRelayLinkedPullRequests(store, issueId, optimisticPullRequests),
      })

      // Dashboard rows keep a capped, denormalized list and a separate count, so they consume an
      // add/remove delta rather than the issue document's full connection replacement. Diff each
      // queued save from its predecessor so rapid selections compose in dispatch order.
      const dashboardUpdate = updateDashboard(optimisticPullRequests)
      return {
        dashboardUpdate,
        relayUpdate,
      }
    },
    getConfirmedOperation: data => {
      const issue = data.linkIssueOrPullRequest?.baseIssueOrPullRequest
      if (issue?.__typename !== 'Issue') return undefined
      return {
        type: 'setFields',
        id: issue.id,
        fields: {closedByPullRequestsReferences: issue.closedByPullRequestsReferences},
      }
    },
    onSuccess: async (data, _variables, context) => {
      const issue = data.linkIssueOrPullRequest?.baseIssueOrPullRequest
      try {
        if (issue?.__typename === 'Issue') {
          const nodes = issue.closedByPullRequestsReferences?.nodes ?? []
          commitLocalUpdate(environment, store => writeRelayLinkedPullRequests(store, issue.id, nodes))
          if (context?.dashboardUpdate) await context.dashboardUpdate.confirm(nodes)
        }
        onCompleted?.()
      } finally {
        context?.relayUpdate.dispose()
      }
    },
    onError: (error, _variables, context) => {
      context?.relayUpdate.dispose()
      context?.dashboardUpdate?.rollback(error)
      onError(error)
    },
  })

  return useCallback(
    (linkingPullRequests: PullRequestPickerPullRequest[]) =>
      mutate({operation: buildOptimisticOperation(issueId, linkingPullRequests)}),
    [issueId, mutate],
  )
}
